import { exactCount, exactInteger } from '@/lib/exact'
import type {
  Block, Cell, Column, DecideReason, FormBlock, NoteBlock, TableBlock,
} from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Configuration › Segments (`cf-segments`), from `GET /v1/admin/segments`
 * with three writes behind it.
 *
 * # A segment grants nothing, and the screen has to say so
 *
 * This is the misreading the whole screen is built to prevent. Declaring a
 * cohort changes no decision:
 *
 * > *"Declared, and it grants and refuses nothing on its own. A segment changes
 * > a decision only once an entitlement rule or a compliance lock names it."*
 *
 * An operator who thinks otherwise defines a cohort, adds customers to it, and
 * expects behaviour to change. Nothing happens, and the failure is silent —
 * there is no error, no refusal and no queue, because from the system's point
 * of view nothing was asked for. So the sentence is on the screen and on the
 * define control, not in a tooltip.
 *
 * # Defining takes no reason code, and that is the schema rather than a lapse
 *
 * `customer_segments` has no reason columns, because declaring a cohort grants
 * nothing on its own. The code appears on the operations that DO change a
 * decision — joining, leaving, setting, locking. A console that added a select
 * here "for consistency" would be requiring a value with nowhere to be stored.
 *
 * # Removing a customer closes an interval; it does not delete anything
 *
 * `POST .../remove` rather than DELETE, and the row stays:
 *
 * > *"a decision taken while the customer was in this cohort stays
 * > explainable."*
 *
 * Same shape, and the same reason, as the account restrict/restore pair.
 *
 * # Membership counts are LIVE, never lifetime
 *
 * `members` counts current memberships. A count that included closed intervals
 * would say a cohort somebody emptied last month still has forty people in it,
 * on the screen an access review reads.
 */

export const SEGMENTS_PATH = '/segments'

export function customerSegmentsPath(customerID: string): string {
  return `/customers/${encodeURIComponent(customerID)}/segments`
}

export function customerSegmentLeavePath(customerID: string, key: string): string {
  return `${customerSegmentsPath(customerID)}/${encodeURIComponent(key)}/remove`
}

const SOURCE = 'postgres · customer_segments (GET /v1/admin/segments)'
const AUTHORITY = 'operational'

/** `entitlement.write` — define, join and leave. Compliance only. */
const PERM_WRITE = 'entitlement.write'
const ROLE = 'Compliance'

// ---------------------------------------------------------------------------
// The read
// ---------------------------------------------------------------------------

interface Segment {
  readonly key: string
  readonly description: string
  readonly createdBy: string
  readonly createdAt: string
  /** Live memberships, or `null` when the backend did not state a number. */
  readonly members: number | null
}

function readSegment(raw: unknown): Segment | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const key = typeof o['segment_key'] === 'string' ? o['segment_key'] : ''
  if (key === '') return null
  return {
    key,
    description: typeof o['description'] === 'string' ? o['description'] : '',
    createdBy: typeof o['created_by'] === 'string' ? o['created_by'] : '',
    createdAt: typeof o['created_at'] === 'string' ? o['created_at'] : '',
    // `exactCount` (#207). A segment's membership is a figure an operator
    // quotes when they decide who a rule reaches; a count wrong in its last
    // places reads as precise and is not, and a negative one was never read.
    members: exactCount(o['members']),
  }
}

const COLS: readonly Column[] = [
  ['Segment key', 'm', 'minmax(180px,1.1fr)'],
  ['What it is for', 'x', 'minmax(260px,2fr)'],
  ['Live members', 'n', '130px'],
  ['Declared by', 'x', 'minmax(150px,1fr)'],
  ['Declared', 'm', '130px'],
]

function row(s: Segment): readonly Cell[] {
  return [
    s.key,
    // An undescribed cohort is a finding, not a blank: the backend refuses a
    // define without one, so a row missing it came from somewhere else.
    s.description === '' ? '— no description on this row' : s.description,
    s.members === null ? '— not stated' : String(s.members),
    s.createdBy === '' ? '— unattributed' : s.createdBy,
    s.createdAt === '' ? '—' : day(s.createdAt),
  ]
}

const GRANTS_NOTHING: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'What a segment does',
  title: 'A segment grants and refuses nothing on its own',
  body: 'Declaring a cohort, and putting customers in it, changes no decision anywhere. A segment starts mattering only once an entitlement rule or a compliance lock names it — until then it is a label. This is worth knowing before you use the controls below, because the failure is silent: there is no error and no queue when a cohort does nothing, since from the system’s point of view nothing was asked for.',
  ref: 'entitlement rules and compliance locks are what read a segment',
}

export function composeSegments(
  payload: Record<string, unknown>, readAt: Date, caller: Caller, api: ApiClient,
): ScreenData {
  const raw = Array.isArray(payload['segments']) ? (payload['segments'] as readonly unknown[]) : []
  const rows: Segment[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readSegment(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const table: TableBlock = {
    t: 'table',
    title: 'Segments',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} segment(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'Member counts are LIVE memberships, not lifetime. A customer removed from a cohort stops being counted, and the membership interval stays on the record so a decision taken while they were in it is still explainable.',
  }

  const blocks: Block[] = [table, GRANTS_NOTHING]

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} segment row(s) could not be read`,
      body: 'They are counted rather than folded in. A row with no segment key is unusable to every control on this screen — the next call needs the key spelled exactly — so it is reported rather than rendered as a blank.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Segments',
      title: 'No cohort has been declared',
      body: 'The endpoint answered with an empty list. Nothing is wrong: a wallet with no cohorts is an ordinary state, and every entitlement rule that would name one simply has none to name.',
      ref: 'a counted zero',
    })
  }

  blocks.push(
    defineForm(api, caller),
    joinForm(api, caller, rows),
    leaveForm(api, caller, rows),
  )

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (rows.some((s) => s.members === null)) missing.push('the membership count on at least one cohort')

  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
    : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing }
  return { blocks, provenance }
}

export const loadSegments: ScreenLoader = async (api) => {
  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(SEGMENTS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The segments endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The segments endpoint returned a shape this console does not know.', null)
  }
  return composeSegments(payload as Record<string, unknown>, new Date(), await calling, api)
}

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

const JOIN_REASONS: readonly DecideReason[] = [
  ['beta_participant', 'Taking part in a limited release', ''],
  ['commercial_agreement', 'Covered by an agreement with this customer', ''],
  ['risk_tier_assignment', 'Placed in this tier by a risk assessment', ''],
  ['support_remediation', 'Placed here while something is put right', ''],
  ['compliance_direction', 'Placed here on a compliance instruction', ''],
]

const LEAVE_REASONS: readonly DecideReason[] = [
  ['beta_ended', 'The limited release has finished', ''],
  ['agreement_ended', 'The agreement that placed them here has ended', ''],
  ['risk_tier_changed', 'A reassessment moved them', ''],
  ['added_in_error', 'They should not have been in this cohort', ''],
  ['customer_request', 'The customer asked to be taken out', ''],
]

/**
 * Declaring a cohort.
 *
 * No reason code, deliberately — see the module header. The control says why,
 * rather than leaving the asymmetry with join and leave to look like an
 * oversight somebody should fix.
 */
function defineForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_WRITE, ROLE, 'Declaring a segment')
  return {
    t: 'form',
    title: 'Declare a segment',
    lead: 'Creates a cohort with a key and a description. It grants nothing and refuses nothing: until an entitlement rule or a compliance lock names this key, putting customers in it changes no decision anywhere.',
    fields: [
      {
        name: 'segment_key', label: 'Segment key', kind: 'text', required: true, mono: true,
        note: 'Normalised by the backend. This is the exact string every rule, lock and membership will name, so it is worth getting right — nothing here renames one.',
      },
      {
        name: 'description', label: 'What it is for', kind: 'textarea', required: true,
        note: 'Required by the backend, and the reason it gives is the one that matters: a cohort nobody described is one whose members nobody can justify at an access review.',
      },
    ],
    reasons: DEFINE_REASONS,
    reasonNote: 'Recorded in the audit row. Note there is no reason CODE on this act — `customer_segments` has no reason columns, because declaring a cohort changes no decision. Codes appear on joining and leaving, which do.',
    auditLine: 'admin.entitlement.segment.define · actor · segment:<key> · reason',
    cta: 'Declare segment',
    ctaNote: 'Nothing changes for any customer until a rule or a lock names this key.',
    blocked,
    ...(blocked === '' ? { submit: defineSubmit(api) } : {}),
  }
}

/**
 * The prose reasons offered for a define.
 *
 * A short list, because there are only so many reasons to declare a cohort and
 * a long one would invite an operator to pick the nearest rather than the
 * right one. `reason` is prose on the wire; these populate the code half of
 * `serialiseReason`, which the backend stores as part of the sentence rather
 * than as a column.
 */
const DEFINE_REASONS: readonly DecideReason[] = [
  ['limited_release', 'For a limited release or beta', ''],
  ['risk_tiering', 'To carry a risk tier', ''],
  ['commercial_tier', 'To carry a commercial arrangement', ''],
  ['compliance_cohort', 'To be named by a compliance lock', ''],
  ['operational_grouping', 'To group customers for an operational reason', ''],
]

function defineSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(SEGMENTS_PATH, {
      segment_key: v['segment_key'] ?? '',
      description: v['description'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The segment was declared',
    (answer) => {
      const key = stringAt(answer, 'segment_key')
      const note = stringAt(answer, 'note')
      return [
        ['segment', key === '' ? (v['segment_key'] ?? '—') : key, 'normalised by the backend'],
        ['members', String(numberAt(answer, 'members') ?? 0), 'a new cohort starts empty'],
        ...(note === '' ? [] : [['what this does', note, ''] as const]),
      ]
    },
  )
}

/**
 * Putting one customer in a cohort.
 *
 * The customer is typed and the segment is chosen. That asymmetry is
 * deliberate: the segments are on screen and the key has to be spelled exactly,
 * so choosing removes a whole class of error — while there is no customer
 * picker on this surface, and inventing one would be a search endpoint this
 * console does not have.
 */
function joinForm(api: ApiClient, caller: Caller, segments: readonly Segment[]): FormBlock {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, 'Adding a customer to a segment')
  const blocked = session !== ''
    ? session
    : segments.length === 0
      ? 'Disabled — no cohort has been declared yet, so there is none to add anybody to. Declare one above first.'
      : ''

  return {
    t: 'form',
    title: 'Add a customer to a segment',
    lead: 'Opens a membership interval for this customer in this cohort. Whether that changes anything for them depends entirely on what names the cohort.',
    fields: [
      {
        name: 'customer_id', label: 'Customer id', kind: 'text', required: true, mono: true,
        note: 'The numeric id from the customer record.',
      },
      {
        name: 'segment_key', label: 'Segment', kind: 'select', required: true,
        options: segments.map((s) => [s.key, `${s.key} — ${s.description === '' ? 'no description' : s.description}`] as const),
        note: 'Chosen rather than typed: the key must match exactly, and every declared cohort is listed above.',
      },
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: JOIN_REASONS.map(([code, gloss]) => [code, `${code} — ${gloss}`] as const),
        note: 'Stored on the membership itself, and separate from the audit reason below: this one stays with the interval and explains it for as long as it exists.',
      },
    ],
    reasons: JOIN_REASONS,
    reasonNote: 'Recorded in the audit row.',
    auditLine: 'admin.entitlement.segment.join · actor · customer:<id> · reason',
    cta: 'Add to segment',
    ctaNote: 'Reversible. Removing them later closes the interval rather than deleting it.',
    blocked,
    ...(blocked === '' ? { submit: joinSubmit(api) } : {}),
  }
}

function joinSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(customerSegmentsPath(v['customer_id'] ?? ''), {
      segment_key: v['segment_key'] ?? '',
      reason_code: v['reason_code'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The customer is now in the cohort',
    (answer) => [
      ['customer', String(numberAt(answer, 'customer_id') ?? (v['customer_id'] ?? '—')), ''],
      ['segment', stringAt(answer, 'segment_key') || (v['segment_key'] ?? '—'), ''],
      ['what changed for them', 'nothing yet, unless a rule or a lock names this cohort', ''],
    ],
  )
}

/** Closing a membership. Never a delete — see the module header. */
function leaveForm(api: ApiClient, caller: Caller, segments: readonly Segment[]): FormBlock {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, 'Removing a customer from a segment')
  const blocked = session !== ''
    ? session
    : segments.length === 0
      ? 'Disabled — no cohort has been declared, so there is no membership to close.'
      : ''

  return {
    t: 'form',
    title: 'Remove a customer from a segment',
    lead: 'Closes this customer’s membership interval. It does not delete anything: the interval stays on the record with when it opened, when it closed and why, so a decision taken while they were in the cohort is still explainable afterwards.',
    fields: [
      {
        name: 'customer_id', label: 'Customer id', kind: 'text', required: true, mono: true,
      },
      {
        name: 'segment_key', label: 'Segment', kind: 'select', required: true,
        options: segments.map((s) => [s.key, s.key] as const),
      },
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: LEAVE_REASONS.map(([code, gloss]) => [code, `${code} — ${gloss}`] as const),
        note: 'Stored on the closed interval.',
      },
    ],
    reasons: LEAVE_REASONS,
    reasonNote: 'Recorded in the audit row.',
    auditLine: 'admin.entitlement.segment.leave · actor · customer:<id> · reason',
    cta: 'Close the membership',
    ctaNote: 'The membership stays readable after it closes. Nothing on this console removes one.',
    blocked,
    ...(blocked === '' ? { submit: leaveSubmit(api) } : {}),
  }
}

function leaveSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(
      customerSegmentLeavePath(v['customer_id'] ?? '', v['segment_key'] ?? ''),
      { reason_code: v['reason_code'] ?? '', reason: v['reason'] ?? '' },
    ),
    'The membership is closed',
    (answer) => {
      const note = stringAt(answer, 'note')
      return [
        ['customer', String(numberAt(answer, 'customer_id') ?? (v['customer_id'] ?? '—')), ''],
        ['segment', stringAt(answer, 'segment_key') || (v['segment_key'] ?? '—'), ''],
        ...(note === '' ? [] : [['on the record', note, ''] as const]),
      ]
    },
  )
}

// ---------------------------------------------------------------------------

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The segment catalogue could not be read',
      body: `${reason} No control is drawn either: every one of them needs a segment key spelled exactly, and this read is where those come from.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function stringAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

function numberAt(o: unknown, key: string): number | null {
  if (typeof o !== 'object' || o === null) return null
  const v = (o as Record<string, unknown>)[key]
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
